/**
 * QualityContent - Content renderer for Quality panel
 * 
 * Displays quality metrics dashboard, compliance status, and audit readiness.
 * Read-only view focused on AS9100D compliance overview.
 */

import { useState } from 'react';
import { Button } from '@/components/ui';
import type { ProjectInstance, UsageMetrics, DocumentAccessLog } from '../../types';

type QualityTab = 'dashboard' | 'compliance' | 'metrics';

interface QualityContentProps {
  projects: ProjectInstance[];
  usageMetrics: UsageMetrics[];
  accessLogs: DocumentAccessLog[];
  loading: boolean;
}

export function QualityContent({
  projects,
  usageMetrics: _usageMetrics,
  accessLogs: _accessLogs,
  loading,
}: QualityContentProps) {
  const [activeTab, setActiveTab] = useState<QualityTab>('dashboard');
  
  // Loading state
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3 mx-auto"></div>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // Calculate metrics
  const totalProjects = projects.length;
  const projectsWithDocControl = projects.filter(p => p.projectCode).length;
  const totalDocuments = projects.reduce((sum, p) => sum + (p.documents?.length || 0), 0);
  const documentsReleased = projects.reduce((sum, p) => 
    sum + (p.documents?.filter(d => d.status === 'Released').length || 0), 0
  );
  const documentsApproved = projects.reduce((sum, p) => 
    sum + (p.documents?.filter(d => d.status === 'Approved').length || 0), 0
  );
  const documentsDraft = projects.reduce((sum, p) => 
    sum + (p.documents?.filter(d => d.status === 'Draft').length || 0), 0
  );
  const qualityScore = totalDocuments > 0 ? Math.round((documentsReleased / totalDocuments) * 100) : 0;
  
  return (
    <div className="flex flex-col h-full">
      {/* Tabs */}
      <div className="flex-shrink-0 px-4">
        <div className="flex gap-4">
          <Button
            onClick={() => setActiveTab('dashboard')}
            variant="ghost"
            className={`py-2 px-1 text-sm font-medium border-b-2 rounded-none h-auto ${
              activeTab === 'dashboard'
                ? 'border-primary text-info'
                : 'border-transparent text-muted-foreground'
            }`}
          >
            Dashboard
          </Button>
          <Button
            onClick={() => setActiveTab('compliance')}
            variant="ghost"
            className={`py-2 px-1 text-sm font-medium border-b-2 rounded-none h-auto ${
              activeTab === 'compliance'
                ? 'border-primary text-info'
                : 'border-transparent text-muted-foreground'
            }`}
          >
            Compliance
          </Button>
          <Button
            onClick={() => setActiveTab('metrics')}
            variant="ghost"
            className={`py-2 px-1 text-sm font-medium border-b-2 rounded-none h-auto ${
              activeTab === 'metrics'
                ? 'border-primary text-info'
                : 'border-transparent text-muted-foreground'
            }`}
          >
            Metrics
          </Button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {activeTab === 'dashboard' && (
          <DashboardView
            totalProjects={totalProjects}
            projectsWithDocControl={projectsWithDocControl}
            totalDocuments={totalDocuments}
            documentsReleased={documentsReleased}
            documentsApproved={documentsApproved}
            documentsDraft={documentsDraft}
            qualityScore={qualityScore}
          />
        )}
        
        {activeTab === 'compliance' && (
          <ComplianceView />
        )}
        
        {activeTab === 'metrics' && (
          <MetricsView
            projects={projects}
            projectsWithDocControl={projectsWithDocControl}
            totalProjects={totalProjects}
            documentsReleased={documentsReleased}
            totalDocuments={totalDocuments}
          />
        )}
      </div>
    </div>
  );
}

// Dashboard view
interface DashboardViewProps {
  totalProjects: number;
  projectsWithDocControl: number;
  totalDocuments: number;
  documentsReleased: number;
  documentsApproved: number;
  documentsDraft: number;
  qualityScore: number;
}

function DashboardView({
  totalProjects,
  projectsWithDocControl,
  totalDocuments,
  documentsReleased,
  documentsApproved,
  documentsDraft,
  qualityScore,
}: DashboardViewProps) {
  return (
    <div className="space-y-4">
      {/* Key Metrics */}
      <div className="grid grid-cols-3 gap-3">
        <MetricCard
          value={totalProjects}
          label="Total Projects"
          sublabel={`${projectsWithDocControl} with Doc Control`}
          color="blue"
        />
        <MetricCard
          value={totalDocuments}
          label="Controlled Docs"
          sublabel="Across all projects"
          color="green"
        />
        <MetricCard
          value={`${qualityScore}%`}
          label="Quality Score"
          sublabel="Documents Released"
          color={qualityScore >= 75 ? 'green' : qualityScore >= 50 ? 'amber' : 'red'}
        />
      </div>
      
      {/* Document Status */}
      <div className="border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Document Status Distribution
        </h3>
        <div className="space-y-2">
          <StatusBar label="Released" count={documentsReleased} total={totalDocuments} color="green" />
          <StatusBar label="Approved" count={documentsApproved} total={totalDocuments} color="blue" />
          <StatusBar label="Draft" count={documentsDraft} total={totalDocuments} color="amber" />
        </div>
      </div>
      
      {/* Quick Info */}
      <div className="bg-info-light border border-info rounded-lg p-3">
        <p className="text-sm text-info">
          <strong>AS9100D Quality Management:</strong> This dashboard provides oversight of 
          document control compliance and quality metrics for aerospace certification projects.
        </p>
      </div>
    </div>
  );
}

// Compliance view
function ComplianceView() {
  return (
    <div className="space-y-4">
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="bg-muted/30 px-4 py-2">
          <h3 className="font-semibold text-foreground">
            AS9100D Clause 7.5 - Documented Information Control
          </h3>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-slate-700">
          <ComplianceRow
            requirement="Documented information required by QMS"
            status="met"
            evidence="Policy templates + project documents"
          />
          <ComplianceRow
            requirement="Identification and description"
            status="met"
            evidence="Unique document numbers"
          />
          <ComplianceRow
            requirement="Review and approval for suitability"
            status="met"
            evidence="Status workflow (Draft → Released)"
          />
          <ComplianceRow
            requirement="Distribution, access, retrieval"
            status="met"
            evidence="Web-based access, search, export"
          />
          <ComplianceRow
            requirement="Retention and disposition"
            status="met"
            evidence="All revisions preserved"
          />
          <ComplianceRow
            requirement="Control of changes"
            status="met"
            evidence="Revision management, audit trail"
          />
        </div>
      </div>
      
      {/* Audit Readiness */}
      <div className="border border-border rounded-lg p-4">
        <h3 className="font-semibold text-foreground mb-3">
          Audit Readiness
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-success">
            <span>✓</span>
            <span>Document Control System</span>
          </div>
          <div className="flex items-center gap-2 text-success">
            <span>✓</span>
            <span>Quality Documentation</span>
          </div>
          <div className="flex items-center gap-2 text-success">
            <span>✓</span>
            <span>Traceability</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Metrics view
interface MetricsViewProps {
  projects: ProjectInstance[];
  projectsWithDocControl: number;
  totalProjects: number;
  documentsReleased: number;
  totalDocuments: number;
}

function MetricsView({
  projects,
  projectsWithDocControl,
  totalProjects,
  documentsReleased,
  totalDocuments,
}: MetricsViewProps) {
  return (
    <div className="space-y-4">
      {/* Document Control Metrics */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="bg-muted/30 px-4 py-2">
          <h3 className="font-semibold text-foreground">
            Document Control Metrics
          </h3>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-slate-700">
          <MetricRow
            metric="Projects with Document Control"
            value={`${projectsWithDocControl}/${totalProjects}`}
            target="100%"
            percentage={totalProjects > 0 ? Math.round((projectsWithDocControl / totalProjects) * 100) : 0}
          />
          <MetricRow
            metric="Documents Released"
            value={`${documentsReleased}/${totalDocuments}`}
            target="Per project plan"
            percentage={totalDocuments > 0 ? Math.round((documentsReleased / totalDocuments) * 100) : 0}
          />
          <MetricRow
            metric="Change History Completeness"
            value={`${projects.filter(p => p.changeHistory && p.changeHistory.length > 0).length}/${totalProjects}`}
            target="100%"
            percentage={totalProjects > 0 ? Math.round((projects.filter(p => p.changeHistory && p.changeHistory.length > 0).length / totalProjects) * 100) : 0}
          />
        </div>
      </div>
      
      {/* Project Quality Status */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="bg-muted/30 px-4 py-2">
          <h3 className="font-semibold text-foreground">
            Project Quality Status
          </h3>
        </div>
        {projects.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground text-sm">
            No projects yet
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-slate-700 max-h-48 overflow-y-auto">
            {projects.slice(0, 10).map(project => {
              const docCount = project.documents?.length || 0;
              const releasedCount = project.documents?.filter(d => d.status === 'Released').length || 0;
              const score = docCount > 0 ? Math.round((releasedCount / docCount) * 100) : 0;
              
              return (
                <div key={project.id} className="px-4 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-foreground truncate">
                      {project.name}
                    </span>
                    {project.projectCode && (
                      <span className="text-xs px-1.5 py-0.5 bg-info-light text-info rounded font-mono">
                        {project.projectCode}
                      </span>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    score === 100 ? 'bg-success-light text-success' :
                    score >= 50 ? 'bg-info-light text-info' :
                    score > 0 ? 'bg-warning-light text-warning' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {score}%
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Metric card component
interface MetricCardProps {
  value: number | string;
  label: string;
  sublabel: string;
  color: 'blue' | 'green' | 'amber' | 'red';
}

function MetricCard({ value, label, sublabel, color }: MetricCardProps) {
  const colorClasses = {
    blue: 'bg-info-light text-info',
    green: 'bg-success-light text-success',
    amber: 'bg-warning-light text-warning',
    red: 'bg-destructive-light text-destructive',
  };
  
  return (
    <div className={`rounded-lg p-3 text-center ${colorClasses[color]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs font-medium opacity-80">{label}</div>
      <div className="text-xs opacity-60 mt-1">{sublabel}</div>
    </div>
  );
}

// Status bar component
interface StatusBarProps {
  label: string;
  count: number;
  total: number;
  color: 'green' | 'blue' | 'amber';
}

function StatusBar({ label, count, total, color }: StatusBarProps) {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  const colorClasses = {
    green: 'bg-success-light',
    blue: 'bg-info',
    amber: 'bg-warning-light',
  };
  
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-muted-foreground w-16">{label}</span>
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full ${colorClasses[color]} transition-all`}
          // eslint-disable-next-line no-restricted-syntax -- Dynamic width for progress bar
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground w-8 text-right">{count}</span>
    </div>
  );
}

// Compliance row component
interface ComplianceRowProps {
  requirement: string;
  status: 'met' | 'partial' | 'not-met';
  evidence: string;
}

function ComplianceRow({ requirement, status, evidence }: ComplianceRowProps) {
  return (
    <div className="px-4 py-2 flex items-center gap-3">
      <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full ${
        status === 'met' ? 'bg-success-light text-success' :
        status === 'partial' ? 'bg-warning-light text-warning' :
        'bg-destructive-light text-destructive'
      }`}>
        {status === 'met' ? '✓ Met' : status === 'partial' ? '◐ Partial' : '✗ Not Met'}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-foreground">{requirement}</div>
        <div className="text-xs text-muted-foreground">{evidence}</div>
      </div>
    </div>
  );
}

// Metric row component
interface MetricRowProps {
  metric: string;
  value: string;
  target: string;
  percentage: number;
}

function MetricRow({ metric, value, target, percentage }: MetricRowProps) {
  return (
    <div className="px-4 py-2 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-foreground">{metric}</div>
      </div>
      <div className="text-sm font-medium text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground w-20 text-right">{target}</div>
      <span className={`text-xs px-2 py-0.5 rounded-full ${
        percentage === 100 ? 'bg-success-light text-success' :
        percentage >= 75 ? 'bg-info-light text-info' :
        percentage >= 50 ? 'bg-warning-light text-warning' :
        'bg-destructive-light text-destructive'
      }`}>
        {percentage}%
      </span>
    </div>
  );
}

